<template>
  <div class="calendar-bar">
    <div class="calendar-bar__label">
      <i class="far fa-calendar-alt mr-50"></i>
      <span>{{ $t('Date range') }}</span>
    </div>

    <div class="calendar-bar__pills">
      <button
          v-for="option in options"
          :key="option"
          type="button"
          class="calendar-bar__pill"
          :class="{ 'calendar-bar__pill--active': selected === option }"
          @click="onOptionSelected(option)"
      >
        <span class="calendar-bar__pill-label">{{ $t(`filters.picker.${option}`) }}</span>
      </button>
    </div>

    <!-- Custom range — the same range picker used by the client filters.
         Click a field to open the calendar (handles its own positioning). -->
    <div class="calendar-bar__custom" :class="{ 'calendar-bar__custom--active': selected === 'custom' }">
      <calendar-picker-range
          v-model="customValue"
          :need-footer="false"
          :clearable="false"
          size="sm"
      />
    </div>
  </div>
</template>


<script>
import CalendarPickerRange from '@/components/widget/picker/calendarPickerRange';

export default {
  name: 'CalendarBar',
  components: { CalendarPickerRange },
  data: () => ({
    options: [
      'all', 'today', 'yesterday', 'thisWeek', 'lastWeek', 'last7Days', 'thisMonth', 'lastMonth', 'last30Days', 'thisYear'
    ],
    selected: 'thisWeek',
    customValue: {
      start: null,
      end: null
    },
  }),
  props: {
    value: {
      default: null
    },
    defaultOption: {
      type: String,
      default: 'thisWeek'
    },
  },
  watch: {
    customValue: {
      handler(val) {
        // Emit only once a full start–end range is chosen.
        if (val && val.start && val.end) {
          this.selected = 'custom';
          this.$emit('input', { from: val.start, until: val.end });
        }
      },
      deep: true,
    },
  },
  created() {
    if (this.options.includes(this.defaultOption)) {
      this.selected = this.defaultOption;
    }
    this.onOptionSelected(this.selected);
  },
  methods: {
    onOptionSelected(option) {
      this.selected = option;
      this.$emit('input', this.getValue(option));
    },
    getValue(option) {
      switch (option) {
        case 'all':
          return { from: this.$moment().subtract(30, 'years').format('YYYY-MM-DD'), until: this.$moment().format('YYYY-MM-DD') };
        case 'today':
          return { from: this.$moment().format('YYYY-MM-DD'), until: this.$moment().format('YYYY-MM-DD') };
        case 'yesterday':
          return { from: this.$moment().subtract(1, 'days').format('YYYY-MM-DD'), until: this.$moment().subtract(1, 'days').format('YYYY-MM-DD') };
        case 'thisWeek':
          return { from: this.$moment().startOf('week').format('YYYY-MM-DD'), until: this.$moment().endOf('week').format('YYYY-MM-DD') };
        case 'lastWeek':
          return { from: this.$moment().subtract(1, 'week').startOf('week').format('YYYY-MM-DD'), until: this.$moment().subtract(1, 'week').endOf('week').format('YYYY-MM-DD') };
        case 'last7Days':
          return { from: this.$moment().subtract(7, 'days').format('YYYY-MM-DD'), until: this.$moment().format('YYYY-MM-DD') };
        case 'thisMonth':
          return { from: this.$moment().startOf('month').format('YYYY-MM-DD'), until: this.$moment().endOf('month').format('YYYY-MM-DD') };
        case 'lastMonth':
          return { from: this.$moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'), until: this.$moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD') };
        case 'last30Days':
          return { from: this.$moment().subtract(30, 'days').format('YYYY-MM-DD'), until: this.$moment().format('YYYY-MM-DD') };
        case 'thisYear':
          return { from: this.$moment().startOf('year').format('YYYY-MM-DD'), until: this.$moment().endOf('year').format('YYYY-MM-DD') };
      }
    },
  }
};
</script>


<style lang="scss" scoped>
.calendar-bar {
  position: relative;
  z-index: 30; // keep the calendar dropdown above the dashboard cards below
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6rem;
  padding: 0.6rem 0.85rem;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.06), rgba(0, 123, 255, 0.02));
  border: 1px solid rgba(0, 123, 255, 0.12);
  border-radius: 14px;
  backdrop-filter: blur(4px);

  &__label {
    display: flex;
    align-items: center;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #b4b7bd;
    padding-right: 0.5rem;
    border-right: 1px solid rgba(110, 117, 159, 0.18);
    margin-right: 0.35rem;

    i { color: #007BFF; }
  }

  &__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
    min-width: 0;
  }

  &__pill {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.38rem 0.85rem;
    min-height: 32px;
    border-radius: 999px;
    border: 1px solid transparent;
    background: transparent;
    color: #6e6b7b;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    outline: none;
    transition: all 0.18s ease;
    white-space: nowrap;

    &:hover { background: rgba(0, 123, 255, 0.08); color: #007BFF; }
    &:focus-visible { box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25); }

    &--active {
      color: #ffffff;
      background: linear-gradient(135deg, #4da3ff 0%, #007BFF 60%, #0062cc 100%);
      box-shadow: 0 6px 14px -6px rgba(0, 123, 255, 0.55);

      &:hover { color: #ffffff; background: linear-gradient(135deg, #66b5ff 0%, #1a8cff 60%, #0066cc 100%); }
    }
  }

  // Custom range picker (start → end fields)
  &__custom {
    margin-left: auto;
    flex: 0 1 300px;
    min-width: 230px;

    ::v-deep .date-picker-range-container {
      .form-control {
        background: #ffffff;
        border-color: rgba(110, 117, 159, 0.22);
        color: #2f2b50;
        font-weight: 600;
        font-size: 0.78rem;
        height: 34px;
      }
      .input-group-text {
        background: #ffffff;
        border-color: rgba(110, 117, 159, 0.22);
        color: #007BFF;
      }
      .input-group:first-child .input-group-text { border-radius: 9px 0 0 9px; }
      .date-picker-arrow { color: #b0b6c8; margin: 0 5px; }
    }

    &--active ::v-deep .date-picker-range-container .form-control,
    &--active ::v-deep .date-picker-range-container .input-group-text {
      border-color: rgba(0, 123, 255, 0.5);
    }
  }
}

// Dark theme
.dark-layout .calendar-bar {
  background: linear-gradient(135deg, rgba(77, 163, 255, 0.10), rgba(77, 163, 255, 0.02));
  border-color: rgba(77, 163, 255, 0.18);

  &__label {
    color: #b4b7bd;
    border-right-color: rgba(255, 255, 255, 0.08);
    i { color: #4da3ff; }
  }

  &__pill {
    color: #b4b7bd;
    &:hover { background: rgba(77, 163, 255, 0.15); color: #b3d7ff; }
  }

  &__custom ::v-deep .date-picker-range-container {
    .form-control { background: #1a2130; border-color: rgba(255, 255, 255, 0.08); color: #d0d2d6; }
    .input-group-text { background: #1a2130; border-color: rgba(255, 255, 255, 0.08); color: #8fc1ff; }
  }
}

@media (max-width: 768px) {
  .calendar-bar {
    gap: 0.4rem;
    padding: 0.5rem 0.6rem;

    &__label { display: none; }
    &__pill { padding: 0.35rem 0.65rem; font-size: 0.74rem; }
    &__custom { margin-left: 0; flex: 1 1 100%; }
  }
}
</style>

<!-- Global: the v-calendar popover renders with a low z-index, so it can be
     painted behind cards/panels. Lift it above everything. -->
<style lang="scss">
.vc-popover-content-wrapper {
  z-index: 1010 !important;
}
</style>
